import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@companion-professional/webutils';

// Typography components are the single place that text styles (font, size, color) are defined.  They follow the
// Shepherd brand guide: Spoof (font-display) for headlines and all-caps captions, Proxima Nova (font-sans) for body
// copy, Blue (text-heading) for headlines, and Navy (text-on-background) for most text.
//
// Every component also exports its variants function (e.g. headingVariants) for elements that can't be swapped for
// the component, such as a react-router <Link /> or a third-party component that only accepts a className.  All
// components accept asChild to render their child element with the typography styles instead of the default element:
//   <TextLink asChild><Link to="/pets">Pets</Link></TextLink>

// Heading ------------------------------------------------------------------------------------------------------------

export const headingVariants = cva(['font-display', 'font-normal', 'leading-display', 'text-heading'], {
  variants: {
    level: {
      // display is for hero/marketing moments (e.g. the checkup landing page).
      display: 'text-3xl md:text-4xl',
      // page is the title of a page.
      page: 'text-2xl md:text-3xl',
      // section is the title of a section, card, or dialog.
      section: 'text-xl',
      // subsection is a smaller title within a section.
      subsection: 'text-lg'
    }
  },
  defaultVariants: {
    level: 'section'
  }
});

const headingElements = {
  display: 'h1',
  page: 'h1',
  section: 'h2',
  subsection: 'h3'
} as const;

export interface HeadingProps extends React.HTMLAttributes<HTMLHeadingElement>, VariantProps<typeof headingVariants> {
  // as overrides the default element for the level (e.g. a section level title rendered as an h3, or a span when the
  // text is styled like a heading but isn't one semantically).
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div';
  asChild?: boolean;
}

// Heading is a brand headline (Spoof, Blue).  The element defaults to h1 for page/display, h2 for section, and h3 for
// subsection.
export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ className, level, as, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : (as ?? headingElements[level ?? 'section']);
    return <Comp ref={ref} className={cn(headingVariants({ level }), className)} {...props} />;
  }
);
Heading.displayName = 'Heading';

// Eyebrow ------------------------------------------------------------------------------------------------------------

export const eyebrowVariants = cva(['font-display', 'uppercase', 'tracking-caption', 'leading-body'], {
  variants: {
    tone: {
      primary: 'text-heading',
      muted: 'text-muted-foreground',
      inherit: ''
    },
    size: {
      sm: 'text-2xs',
      md: 'text-xs'
    }
  },
  defaultVariants: {
    tone: 'primary',
    size: 'md'
  }
});

export interface EyebrowProps extends React.HTMLAttributes<HTMLElement>, VariantProps<typeof eyebrowVariants> {
  asChild?: boolean;
}

// Eyebrow is the brand all-caps caption (Spoof, 10% tracking).  Use it for small labels above headlines, table column
// headers, and field labels in read-only detail views.
export const Eyebrow = React.forwardRef<HTMLElement, EyebrowProps>(
  ({ className, tone, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'p';
    return (
      <Comp
        ref={ref as React.Ref<HTMLParagraphElement>}
        className={cn(eyebrowVariants({ tone, size }), className)}
        {...props}
      />
    );
  }
);
Eyebrow.displayName = 'Eyebrow';

// Text ---------------------------------------------------------------------------------------------------------------

export const textVariants = cva(['font-sans'], {
  variants: {
    size: {
      xs: 'text-xs',
      sm: 'text-sm',
      md: 'text-md',
      base: 'text-base',
      lg: 'text-lg'
    },
    tone: {
      // default inherits the surrounding text color (Navy on the standard surfaces).
      default: '',
      muted: 'text-muted-foreground',
      primary: 'text-primary',
      destructive: 'text-destructive'
    },
    weight: {
      regular: 'font-normal',
      strong: 'font-semibold'
    },
    // relaxed is the brand's 160% line spacing for paragraphs of body copy.
    leading: {
      tight: 'leading-tight',
      normal: 'leading-normal',
      relaxed: 'leading-body'
    }
  },
  defaultVariants: {
    size: 'md',
    tone: 'default',
    weight: 'regular',
    leading: 'normal'
  }
});

export interface TextProps extends React.HTMLAttributes<HTMLElement>, VariantProps<typeof textVariants> {
  as?: 'p' | 'span' | 'div' | 'label';
  asChild?: boolean;
}

// Text is body copy (Proxima Nova).  Use tone="muted" for secondary/supporting text instead of gray utility classes.
export const Text = React.forwardRef<HTMLElement, TextProps>(
  ({ className, size, tone, weight, leading, as = 'p', asChild = false, ...props }, ref) => {
    // The element varies with `as`, so the component is typed loosely here; the public ref type is HTMLElement.
    const Comp = (asChild ? Slot : as) as React.ElementType;
    return <Comp ref={ref} className={cn(textVariants({ size, tone, weight, leading }), className)} {...props} />;
  }
);
Text.displayName = 'Text';

// TextLink -----------------------------------------------------------------------------------------------------------

export const linkVariants = cva(
  [
    'text-link',
    'underline',
    'underline-offset-2',
    'decoration-link/40',
    'transition-colors',
    'hover:decoration-link',
    'focus-visible:outline-none',
    'focus-visible:ring-2',
    'focus-visible:ring-ring',
    'focus-visible:ring-offset-2',
    'rounded-sm',
    'cursor-pointer'
  ],
  {
    variants: {
      tone: {
        default: '',
        // inherit keeps the surrounding text color (e.g. links on a colored or inverted background).
        inherit: 'text-inherit decoration-current'
      }
    },
    defaultVariants: {
      tone: 'default'
    }
  }
);

export interface TextLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>,
    VariantProps<typeof linkVariants> {
  asChild?: boolean;
}

// TextLink is an inline text link.  For router links, wrap the router component: <TextLink asChild><Link .../></TextLink>
export const TextLink = React.forwardRef<HTMLAnchorElement, TextLinkProps>(
  ({ className, tone, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'a';
    return <Comp ref={ref} className={cn(linkVariants({ tone }), className)} {...props} />;
  }
);
TextLink.displayName = 'TextLink';
